import * as api from "@/api";
import LocalStorage from "@/classes/local-storage";
import QueryDataManager from "@/classes/query-data-manager";
import BingWallpaperInfoPopup from "@/components/bing-wallpaper-info-popup";
import styles from "@/components/corner-buttons.module.css";
import LoginTipPopup from "@/components/login-tip-popup";
import { globals } from "@/globals";
import useBingWallpaper from "@/hooks/use-bing-wallpaper";
import * as signals from "@/signals";
import { PositionInfo } from "@/types";
import {
	downloadExtension,
	encodeData,
	getDisplayName,
	logIn,
	openPopup,
	showMenu,
} from "@/utils";
import {
	faChrome,
	faEdge,
	faFirefox,
} from "@fortawesome/free-brands-svg-icons";
import {
	faBell,
	faCircleInfo,
	faGear,
	faHeart,
	faImage,
	faMagnifyingGlass,
	faQuestionCircle,
	faRightFromBracket,
	faUserCircle,
	IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";

function CornerButtons(): JSX.Element {
	const queryClient = useQueryClient();

	const accountButtonRef = useRef<HTMLButtonElement>(null);
	const settingsButtonRef = useRef<HTMLButtonElement>(null);

	const [bingWallpaperInfoPopup, setBingWallpaperInfoPopup] = useState({
		isClosing: false,
		isShown: false,
	});
	const [isBounceAnimationBeingPlayed, setIsBounceAnimationBeingPlayed] =
		useState<boolean>(false);
	const [isDotHidden, setIsDotHidden] = useState<boolean>(false);
	const [loginTipPopup, setLoginTipPopup] = useState({
		isClosing: false,
		isShown: false,
	});

	const { bingWallpaperResponse } = useBingWallpaper();

	const shouldShowBrowserExtensionMenuItem =
		!globals.isExtension &&
		!globals.isMobile &&
		(globals.isChrome || globals.isFirefox);

	const closeBingWallpaperInfoPopup = (): void => {
		setBingWallpaperInfoPopup({
			isClosing: false,
			isShown: false,
		});
	};

	const closeLoginTipPopup = (): void => {
		setLoginTipPopup({
			isClosing: false,
			isShown: false,
		});
	};

	const encodeToken = (): string => {
		const preEncodedToken = `${signals.login.value.token},,${Math.round(Date.now() / 1000)}`;
		let postEncodedToken = "";
		for (let i = 0; i < preEncodedToken.length; i++) {
			postEncodedToken += String.fromCharCode(
				preEncodedToken.charCodeAt(i) + 23,
			);
		}
		return window.btoa(postEncodedToken).replace(/=+$/, "");
	};

	const getMenuPosition = (element: HTMLButtonElement): PositionInfo => {
		const rect = element.getBoundingClientRect();
		return {
			left: rect.left + rect.width,
			top: rect.bottom + 5,
		};
	};

	const handleAccountClick = (event: MouseEvent<HTMLButtonElement>): void => {
		if (!signals.login.value.username) {
			logIn();
			startClosingLoginTipPopup();
			return;
		}
		if (!accountButtonRef.current) {
			return;
		}
		const position = getMenuPosition(accountButtonRef.current);
		showMenu(
			event,
			[
				{
					content: {
						html: renderToStaticMarkup(
							<>
								<div className={styles["user-display-name"]}>
									{getDisplayName()}
								</div>
								<div>{t("accountSettings")}</div>
							</>,
						),
						icon: faUserCircle,
						text: t("accountSettings"),
					},
					onClick: (): void => {
						window.open(
							globals.BACKEND_CENTRAL +
								"/sso?" +
								encodeData({
									callback: "https://account.retiehe.com/",
									token: encodeToken(),
									username: signals.login.value.username,
								}),
						);
					},
					when: true,
				},
				{
					content: {
						icon: faRightFromBracket,
						text: t("logOut"),
					},
					onClick: async (): Promise<void> => {
						signals.isLoadingShown.value = true;
						try {
							await api.logOut();
							LocalStorage.deletePrivateData();
							window.location.reload();
						} catch (error) {
							void api.handleApiError(error);
						} finally {
							signals.isLoadingShown.value = false;
						}
					},
					when: true,
				},
			],
			{
				x: position.left,
				y: position.top,
			},
		);
	};

	const handleImportantMessageClick = (): void => {
		signals.cornerMessage.value = {
			...cornerMessage,
			isShown: true,
		};
	};

	const handleLikeWallpaperFocus = (): void => {
		if (isBounceAnimationBeingPlayed) {
			// Avoid showing again when the focus comes back from the dialog
			return;
		}
		showBingWallpaperInfoPopup();
	};

	const handleSettingsClick = (
		event: MouseEvent<HTMLButtonElement>,
	): void => {
		if (!settingsButtonRef.current) {
			return;
		}
		const position = getMenuPosition(settingsButtonRef.current);
		showMenu(
			event,
			[
				{
					content: {
						hasPopup: true,
						icon: faGear,
						text: t("generalSettings"),
					},
					onClick: (): void => {
						openPopup("generalSettings");
					},
					when: true,
				},
				{
					content: {
						hasPopup: true,
						icon: faMagnifyingGlass,
						text: t("searchEnginePreferences"),
					},
					onClick: (): void => {
						openPopup("searchEngine");
					},
					when: true,
				},
				{
					content: {
						hasPopup: true,
						icon: faImage,
						text: t("wallpaperPreferences"),
					},
					onClick: (): void => {
						openPopup("wallpaper");
					},
					when: true,
				},
				{
					content: {
						divider: true,
					},
					when: true,
				},
				{
					content: {
						hasPopup: true,
						icon: faHeart,
						text: t("donateUs"),
					},
					onClick: (): void => {
						openPopup("donate");
					},
					when: true,
				},
				{
					content: {
						hasPopup: true,
						icon: faQuestionCircle,
						text: t("feedback"),
					},
					onClick: (): void => {
						openPopup("feedback");
					},
					when: true,
				},
				{
					content: {
						hasPopup: true,
						icon: faCircleInfo,
						text: t("about"),
					},
					onClick: (): void => {
						openPopup("about");
					},
					when: true,
				},
				{
					content: {
						divider: true,
					},
					when: shouldShowBrowserExtensionMenuItem,
				},
				{
					content: {
						icon: ((): IconDefinition => {
							if (globals.isEdge) {
								return faEdge;
							} else if (globals.isFirefox) {
								return faFirefox;
							} else {
								return faChrome;
							}
						})(),
						text: t("browserExtension"),
					},
					onClick: downloadExtension,
					when: shouldShowBrowserExtensionMenuItem,
				},
			],
			{
				isModal: !globals.isIos, // iOS has a scrolling bug with modal
				x: position.left,
				y: position.top,
			},
		);
	};

	const likeBingWallpaper = async (): Promise<void> => {
		setIsBounceAnimationBeingPlayed(true);
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).likeBingWallpaper({
				bingWallpaperResponse,
			});
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const showBingWallpaperInfoPopup = (): void => {
		setBingWallpaperInfoPopup({
			isClosing: false,
			isShown: true,
		});
	};

	const showLoginTipPopup = (): void => {
		if (signals.login.value.username) {
			return;
		}
		setIsDotHidden(true);
		setLoginTipPopup({
			isClosing: false,
			isShown: true,
		});
	};

	const startClosingBingWallpaperInfoPopup = (): void => {
		if (!bingWallpaperInfoPopup.isShown) {
			return;
		}
		setBingWallpaperInfoPopup({
			isClosing: true,
			isShown: true,
		});
	};

	const startClosingLoginTipPopup = (): void => {
		if (!loginTipPopup.isShown) {
			return;
		}
		setLoginTipPopup({
			isClosing: true,
			isShown: true,
		});
	};

	const cornerMessage = signals.cornerMessage.value;
	return (
		<div className={styles["corner-buttons-container"]}>
			{cornerMessage.text && cornerMessage.title && (
				<button
					aria-haspopup="dialog"
					className={styles["corner-button"]}
					onClick={handleImportantMessageClick}
					title={t("importantMessage")}
					type="button"
				>
					<FontAwesomeIcon icon={faBell} />
				</button>
			)}
			{signals.generalSettings.value.currentWallpaper === "Bing" && (
				<button
					aria-haspopup="dialog"
					className={clsx(
						styles["corner-button"],
						styles["like-button"],
						isBounceAnimationBeingPlayed && "bounce",
					)}
					onBlur={startClosingBingWallpaperInfoPopup}
					onClick={() => void likeBingWallpaper()}
					onFocus={handleLikeWallpaperFocus}
					onMouseEnter={showBingWallpaperInfoPopup}
					onMouseLeave={startClosingBingWallpaperInfoPopup}
					type="button"
				>
					<FontAwesomeIcon icon={faHeart} />
					<span>{bingWallpaperResponse?.likeCount}</span>
				</button>
			)}
			<button
				aria-haspopup={signals.login.value.username ? "menu" : "dialog"}
				className={styles["corner-button"]}
				onBlur={startClosingLoginTipPopup}
				onClick={handleAccountClick}
				onFocus={showLoginTipPopup}
				onMouseEnter={showLoginTipPopup}
				onMouseLeave={startClosingLoginTipPopup}
				ref={accountButtonRef}
				title={t("account")}
				type="button"
			>
				<FontAwesomeIcon icon={faUserCircle} />
				{!signals.login.value.username && (
					<span
						className={clsx(
							styles["dot"],
							isDotHidden && styles["hidden"],
						)}
					></span>
				)}
			</button>
			<button
				aria-haspopup="menu"
				className={clsx(styles["corner-button"], styles["rotate"])}
				onClick={handleSettingsClick}
				ref={settingsButtonRef}
				title={t("settings")}
				type="button"
			>
				<FontAwesomeIcon icon={faGear} />
			</button>
			<BingWallpaperInfoPopup
				{...bingWallpaperInfoPopup}
				closePopup={closeBingWallpaperInfoPopup}
			/>
			<LoginTipPopup
				{...loginTipPopup}
				closePopup={closeLoginTipPopup}
			/>
		</div>
	);
}

export default CornerButtons;
